import React, { useState, useEffect } from 'react';
import api from '../seller-apis/api';
import { useDispatch } from 'react-redux';
import {
  ensureVendorFrozenCheck,
  refreshVendorFrozenCheck,
} from '../Actions/bootstrapVendorFrozenCheck';

const Frozen = () => {
  const dispatch = useDispatch();
  const [requestSubmitted, setRequestSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [daysSinceLastOrder, setDaysSinceLastOrder] = useState(0);
  const [tmpStatus, setTmpStatus] = useState(false);
  const [hasExistingRequest, setHasExistingRequest] = useState(false);
  const [pendingRequest, setPendingRequest] = useState(null);
  const [rejectedRequest, setRejectedRequest] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    checkFreezeStatus();
  }, []);
  // Check current freeze status from API
  const checkFreezeStatus = async (isRefresh = false) => {
    if (isRefresh) {
      setIsRefreshing(true);
    } else {
      setIsLoading(true);
    }
    
    try {
        const response = isRefresh
          ? await refreshVendorFrozenCheck(dispatch)
          : await ensureVendorFrozenCheck(dispatch);
        
        // Handle temporarily inactive vendors (tmpStatus = true)
        if (response.message === "seller temporarily inactive") {
          const data = response.data;
          
          setTmpStatus(data.tmpStatus);
          setDaysSinceLastOrder(data.daysSinceLastOrder || 0);
          
          // Handle vendor request (both pending and rejected)
          if (data.hasVendorRequest && data.vendorRequest) {
            const request = data.vendorRequest;
            
            if (request.status === 'OPEN') {
              // Pending request
              setHasExistingRequest(true);
              setRequestSubmitted(true);
              setPendingRequest(request);
            } else if (request.status === 'REJECT' || request.status === 'REJECTED') {
              // Rejected request
              setRejectedRequest(request);
              setPendingRequest(request);
            } else if (request.status === 'APPROVED') {
              // Approved request
              setPendingRequest(request);
            }
          }
          
        } else if (response.message === "seller fetched") {
          // If vendor is active, they shouldn't be on this page
          window.location.href = "/home";
          return;
        } else if (response.message === "Seller Not Found") {
          window.location.href = "/login";
          return;
        } else if (response.message === "Seller Not Activated") {
          // Handle non-activated seller case
          setTmpStatus(false);
        }
        
        if (isRefresh) {
          setIsRefreshing(false);
        } else {
          setIsLoading(false);
        }
        
      } catch (error) {
        console.error('❌ Frozen: Error checking freeze status:', error);
        
        // Fallback to localStorage if API fails
        const freezeDetailsString = localStorage.getItem("freezeDetails");
        
        if (freezeDetailsString) {
          try {
            const freezeDetails = JSON.parse(freezeDetailsString);
            
            setDaysSinceLastOrder(freezeDetails.daysSinceLastOrder || 0);
            setTmpStatus(true); // Assume frozen if on this page
            
            if (freezeDetails.hasRequestedUnfreeze) {
              setHasExistingRequest(true);
              setRequestSubmitted(true);
            }
          } catch (parseError) {
            console.error('❌ Frozen: Error parsing freezeDetails:', parseError);
            // Set minimal defaults if everything fails
            setTmpStatus(true);
            setDaysSinceLastOrder(0);
          }
        } else {
          // Set minimal defaults if no data available
          setTmpStatus(true);
          setDaysSinceLastOrder(0);
        }
        
        if (isRefresh) {
          setIsRefreshing(false);
        } else {
          setIsLoading(false);
        }
      }
    };

    const handleRefresh = () => {
    checkFreezeStatus(true);
  };
  const handleUnfreezeRequest = async () => {
    setIsSubmitting(true);
    try {
      const payload = {
        reason: `Account unfreeze request - Temporarily inactive for ${daysSinceLastOrder} days`
      };

      const response = await api.auth.requestUnFreeze(payload);
      
      if (response.message === 'Unfreeze request submitted successfully') {
        setRequestSubmitted(true);
        setHasExistingRequest(true);
        setPendingRequest({
          requestId: response.data.requestId,
          requestDate: response.data.requestDate,
          reason: response.data.reason,
          status: response.data.status
        });
        // Clear rejected request when new request is submitted
        setRejectedRequest(null);
      } else {
        throw new Error(response.message || 'Failed to submit request');
      }
      
    } catch (error) {
      console.error('❌ Frozen: Error submitting unfreeze request:', error);
      
      if (error.response && error.response.data && error.response.data.message) {
        const errorMessage = error.response.data.message;
        
        if (errorMessage.includes('already pending')) {
          setHasExistingRequest(true);
          setRequestSubmitted(true);
          alert('You already have a pending unfreeze request. Please wait for admin approval.');
        } else {
          alert(errorMessage);
        }
      } else {
        alert('Failed to submit unfreeze request. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get appropriate status message
  const getStatusMessage = () => {
    return 'Your seller panel has been temporarily frozen due to inactivity (60+ days without orders)';
  };

  const getUnfreezeButtonText = () => {
    if (isSubmitting) return 'Submitting...';
    
    if (hasExistingRequest && pendingRequest?.status === 'OPEN') {
      return 'Request Already Sent to Admin';
    }
    
    if (pendingRequest?.status === 'REJECT' || pendingRequest?.status === 'REJECTED') {
      return 'Request to Unfreeze Panel Again';
    }
    
    if (pendingRequest?.status === 'APPROVED' || pendingRequest?.status === 'CLOSE') {
      return 'Request Approved';
    }
    
    if (requestSubmitted || hasExistingRequest) {
      return `Request ${pendingRequest?.status === 'CLOSE' ? 'Approved' : pendingRequest?.status || 'Sent'}`;
    }
    
    return 'Request to Unfreeze Panel';
  };

  const isButtonDisabled = () => {
    if (isSubmitting) return true;
    
    if (hasExistingRequest && pendingRequest?.status === 'OPEN') {
      return true;
    }
    
    // Enable button for rejected requests (allow new request)
    if (pendingRequest?.status === 'REJECT' || pendingRequest?.status === 'REJECTED') {
      return false;
    }
    
    if (pendingRequest?.status === 'APPROVED' || pendingRequest?.status === 'CLOSE') {
      return true;
    }
    
    return false;
  };

  const getLastOrderText = () => {
    if (daysSinceLastOrder === 0) {
      return 'Last order: Today';
    } else if (daysSinceLastOrder === 1) {
      return 'Last order: 1 day ago';
    } else if (daysSinceLastOrder > 0) {
      return `Last order: ${daysSinceLastOrder} days ago`;
    } else {
      return 'No orders found';
    }
  };

  const formatRequestDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusInfo = () => {
    if (!pendingRequest) return null;

    if (pendingRequest.status === 'REJECT' || pendingRequest.status === 'REJECTED') {
      return {
        type: 'error',
        icon: '❌',
        title: 'Request Rejected',
        message: 'Your previous request was rejected. You can submit a new request.',
        bgColor: '#fef2f2',
        borderColor: '#fecaca',
        textColor: '#dc2626'
      };
    }

    if (pendingRequest.status === 'OPEN') {
      return {
        type: 'pending',
        icon: '⏳',
        title: 'Request Pending',
        message: `Your unfreeze request was submitted on ${formatRequestDate(pendingRequest.requestDate)} and is currently under review by our admin team. Please wait for approval.`,
        bgColor: '#fff7ed',
        borderColor: '#fed7aa',
        textColor: '#c2410c'
      };
    }

    if (pendingRequest.status === 'APPROVED') {
      return {
        type: 'success',
        icon: '✅',
        title: 'Request Approved',
        message: 'Your unfreeze request has been approved! Your account should be active soon.',
        bgColor: '#f0f9f4',
        borderColor: '#10b981',
        textColor: '#065f46'
      };
    }

    // Default for newly submitted
    if (requestSubmitted || hasExistingRequest) {
      return {
        type: 'success',
        icon: '✅',
        title: 'Request Submitted Successfully!',
        message: `Your unfreeze request was submitted on ${formatRequestDate(pendingRequest.requestDate)}. Our team will review it and get back to you within 24-48 hours.`,
        bgColor: '#f0f9f4',
        borderColor: '#10b981',
        textColor: '#065f46'
      };
    }

    return null;
  };

  // Show loading state
  if (isLoading) {
    return (
      <div style={{ 
        padding: '20px', 
        minHeight: '100vh', 
        backgroundColor: '#ffffff',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center' 
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ 
            width: '40px', 
            height: '40px', 
            border: '3px solid #f3f3f3', 
            borderTop: '3px solid #dc2626',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }}></div>
          <p style={{ color: '#6b7280', fontSize: '14px' }}>Loading account status...</p>
        </div>
      </div>
    );
  }

  const statusInfo = getStatusInfo();

  return (
    <div style={{ 
      padding: '20px', 
      minHeight: '100vh', 
      backgroundColor: '#ffffff',
      position: 'relative',
      overflow: 'hidden'
    }}>

      {/* Top Alert Banner */}
      <div 
        style={{ 
          backgroundColor: '#fef2f2', 
          border: '1px solid #fecaca',
          borderRadius: '8px',
          padding: '16px',
          marginBottom: '24px',
          position: 'relative',
          zIndex: 10
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start' }}>
            {/* Red circle icon */}
            <div 
              style={{
                width: '20px',
                height: '20px',
                backgroundColor: '#dc2626',
                borderRadius: '50%',
                marginRight: '12px',
                marginTop: '2px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <span style={{ color: 'white', fontSize: '12px', fontWeight: 'bold' }}>!</span>
            </div>
            
            <div>
              <div style={{ color: '#dc2626', fontSize: '16px', fontWeight: '600', marginBottom: '4px' }}>
                Panel Temporarily Frozen
              </div>
              <div style={{ color: '#b91c1c', fontSize: '14px', marginBottom: '12px' }}>
                {getStatusMessage()}
              </div>
              
              <button 
                onClick={handleUnfreezeRequest}
                disabled={isButtonDisabled()}
                style={{
                  backgroundColor: 'transparent',
                  border: '1px solid #dc2626',
                  color: '#dc2626',
                  padding: '6px 12px',
                  borderRadius: '4px',
                  fontSize: '13px',
                  cursor: isButtonDisabled() ? 'not-allowed' : 'pointer',
                  opacity: isButtonDisabled() ? 0.6 : 1,
                  fontWeight: '500',
                  marginRight: '8px'
                }}
              >
                {getUnfreezeButtonText()}
              </button>
              
              <button 
                onClick={handleRefresh}
                disabled={isRefreshing}
                style={{
                  backgroundColor: 'transparent',
                  border: '1px solid #6b7280',
                  color: '#6b7280',
                  padding: '6px 12px',
                  borderRadius: '4px',
                  fontSize: '13px',
                  cursor: isRefreshing ? 'not-allowed' : 'pointer',
                  opacity: isRefreshing ? 0.6 : 1,
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                <svg 
                  width="12" 
                  height="12" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2"
                  style={{ 
                    animation: isRefreshing ? 'spin 1s linear infinite' : 'none'
                  }}
                >
                  <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/>
                  <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/>
                </svg>
                {isRefreshing ? 'Refreshing...' : 'Refresh'}
              </button>
            </div>
          </div>
          
          {/* Right side - days count */}
          <div style={{ fontSize: '13px', color: '#9ca3af', textAlign: 'right' }}>
            {getLastOrderText()}
          </div>
        </div>
      </div>

      {/* Status message */}
      {statusInfo && (
        <div 
          style={{ 
            backgroundColor: statusInfo.bgColor, 
            border: `1px solid ${statusInfo.borderColor}`,
            borderRadius: '6px',
            padding: '12px 16px',
            marginBottom: '24px',
            fontSize: '14px',
            color: statusInfo.textColor
          }}
        >
          <strong>
            {statusInfo.icon} {statusInfo.title}
          </strong>
          <div style={{ fontSize: '12px', marginTop: '4px' }}>
            {statusInfo.message}
          </div>
          
          {/* Show rejection details if available */}
          {rejectedRequest && statusInfo.type === 'error' && (
            <div style={{ fontSize: '11px', marginTop: '8px', opacity: 0.8 }}>
              <div>Request ID: {rejectedRequest.requestId}</div>
              <div>Submitted on: {new Date(rejectedRequest.requestDate).toLocaleDateString()}</div>
            </div>
          )}
          
          {pendingRequest?.status === 'OPEN' && (
            <div style={{ fontSize: '11px', marginTop: '8px', fontStyle: 'italic', opacity: 0.8 }}>
              💡 <strong>Note:</strong> The "Request to Unfreeze Panel" button is disabled because your request is already under review.
            </div>
          )}
        </div>
      )}

      {/* Dashboard Content Area with Lock Overlay */}
      <div style={{ 
        position: 'relative',
        minHeight: '500px',
        backgroundColor: '#f8f9fa',
        borderRadius: '8px',
        padding: '20px'
      }}>
        
        {/* Blurred Dashboard Content */}
        <div style={{ 
          filter: 'blur(2px)',
          opacity: '0.3',
          pointerEvents: 'none'
        }}>
          {/* Simulated dashboard cards */}
          <div style={{ display: 'flex', gap: '20px', marginBottom: '30px' }}>
            <div style={{ 
              backgroundColor: '#e8f5e8', 
              padding: '20px', 
              borderRadius: '8px', 
              flex: 1,
              minHeight: '100px'
            }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#065f46', marginBottom: '8px' }}>₹0</div>
              <div style={{ fontSize: '14px', color: '#065f46' }}>Today's Earnings</div>
            </div>
            <div style={{ 
              backgroundColor: '#e0f2fe', 
              padding: '20px', 
              borderRadius: '8px', 
              flex: 1,
              minHeight: '100px'
            }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#0369a1', marginBottom: '8px' }}>0</div>
              <div style={{ fontSize: '14px', color: '#0369a1' }}>New Orders</div>
            </div>
            <div style={{ 
              backgroundColor: '#fef3e2', 
              padding: '20px', 
              borderRadius: '8px', 
              flex: 1,
              minHeight: '100px'
            }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#c2410c', marginBottom: '8px' }}>0</div>
              <div style={{ fontSize: '14px', color: '#c2410c' }}>Pending Actions</div>
            </div>
          </div>

          {/* Simulated chart areas */}
          <div style={{ display: 'flex', gap: '20px' }}>
            <div style={{ 
              backgroundColor: '#ffffff', 
              padding: '20px', 
              borderRadius: '8px', 
              flex: 1,
              minHeight: '200px',
              border: '1px solid #e5e7eb'
            }}>
              <div style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px' }}>Sales Overview</div>
              <div style={{ height: '150px', backgroundColor: '#f3f4f6', borderRadius: '4px' }}></div>
            </div>
            <div style={{ 
              backgroundColor: '#ffffff', 
              padding: '20px', 
              borderRadius: '8px', 
              flex: 1,
              minHeight: '200px',
              border: '1px solid #e5e7eb'
            }}>
              <div style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px' }}>Recent Orders</div>
              <div style={{ height: '150px', backgroundColor: '#f3f4f6', borderRadius: '4px' }}></div>
            </div>
          </div>
        </div>

        {/* Dashboard Locked Overlay */}
        <div style={{ 
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          textAlign: 'center',
          zIndex: 20,
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          padding: '40px',
          borderRadius: '12px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
          border: '1px solid #e5e7eb'
        }}>
          {/* Lock Icon */}
          <div 
            style={{
              width: '60px',
              height: '60px',
              backgroundColor: '#fef2f2',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px',
              border: '2px solid #fecaca'
            }}
          >
            <svg 
              width="24" 
              height="24" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="#dc2626"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
              <circle cx="12" cy="16" r="1"/>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
            </svg>
          </div>

          {/* Dashboard Locked Text */}
          <h2 
            style={{ 
              fontSize: '20px', 
              fontWeight: '600', 
              color: '#1f2937', 
              marginBottom: '12px',
              margin: '0 0 12px 0'
            }}
          >
            Dashboard Temporarily Locked
          </h2>

          <p 
            style={{ 
              fontSize: '14px', 
              color: '#6b7280', 
              marginBottom: '16px',
              lineHeight: '1.5',
              margin: '0 0 16px 0',
              maxWidth: '300px'
            }}
          >
            Your seller panel has been temporarily locked due to inactivity. Please
            request to unlock your panel to access all features.
          </p>

          {/* Warning icon with last order info */}
          <div 
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '12px',
              color: '#9ca3af',
              backgroundColor: '#f9fafb',
              padding: '8px 12px',
              borderRadius: '6px',
              border: '1px solid #e5e7eb'
            }}
          >
            <svg 
              width="14" 
              height="14" 
              viewBox="0 0 24 24" 
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ marginRight: '6px' }}
            >
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
              <line x1="12" y1="9" x2="12" y2="13"/>
              <line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
            {getLastOrderText()}
          </div>
        </div>
      </div>

      {/* CSS Animation for loading spinner */}
      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default Frozen;